// src/ui/components/fatigueChart.ts — SVG Fatigue Chart & Impulsive Guess Visualizer (FR-DASH-003, FR-DASH-004)
// Renders accuracy & impulsive-guess curves across minutes 1–10, 11–20, and 21–30,
// and provides one-click JSON data export for parent analysis.

import { tokens } from '@ui/designTokens';
import { soundEngine } from '@audio/soundEngine';
import {
  TelemetryCollector,
  type CognitiveFatigueAnalysis,
} from '@storage/telemetryCollector';

export interface FatigueChartOptions {
  analysis?: CognitiveFatigueAnalysis;
  width?: number;
  height?: number;
}

export class FatigueChart {
  private container: HTMLElement;
  private analysis: CognitiveFatigueAnalysis;
  private width: number;
  private height: number;

  constructor(options: FatigueChartOptions = {}) {
    this.container = document.createElement('div');
    this.container.id = 'fatigue-analytics-deck';
    this.container.className = 'fatigue-chart-container';
    this.width = options.width ?? 520;
    this.height = options.height ?? 240;
    this.analysis = options.analysis ?? TelemetryCollector.analyzeFatigue();
  }

  setAnalysis(analysis: CognitiveFatigueAnalysis): void {
    this.analysis = analysis;
    this.render();
  }

  render(): HTMLElement {
    const { segments, parentInsight, totalEvents, overallImpulsivePercent, fatigueOnsetDetected } = this.analysis;

    this.container.innerHTML = `
      <section class="morris-card" style="padding: 28px; margin-bottom: 24px;">
        <!-- Header & JSON Export -->
        <div style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 14px; margin-bottom: 20px;">
          <div>
            <div style="display: inline-flex; align-items: center; gap: 8px; font-size: 0.85rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase;">
              🧠 Cognitive Fatigue Curve (Mins 1–10 · 11–20 · 21–30)
            </div>
            <h3 style="font-family: ${tokens.font.heading}; font-size: 1.6rem; color: ${tokens.color.morrisIndigo}; margin: 4px 0 6px;">
              Pacing & Impulsive Guess Analysis
            </h3>
            <p style="margin: 0; font-size: 0.95rem; color: #4A5B73;">
              Tracks accuracy decay and rapid sub-1.5s impulsive guessing (${overallImpulsivePercent}% rapid across ${totalEvents} logged attempts).
            </p>
          </div>

          <button
            id="btn-export-telemetry-json"
            class="touch-target"
            style="display: inline-flex; align-items: center; gap: 8px; padding: 10px 18px; font-size: 0.95rem; font-weight: 800; border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 12px; background: #FFFDF9; color: ${tokens.color.morrisIndigo}; cursor: pointer; box-shadow: 0 2px 8px rgba(7, 20, 36, 0.06);"
            title="Export full session archive to JSON"
          >
            📥 Export Session JSON
          </button>
        </div>

        <!-- SVG Fatigue Chart Stage -->
        <div style="background: rgba(255, 253, 249, 0.98); border: 1.5px solid ${tokens.color.gildedBrass}; border-radius: 16px; padding: 20px 16px 16px; margin-bottom: 24px; box-shadow: 0 4px 16px rgba(10, 25, 47, 0.06);">
          ${this.renderSvgChart()}

          <!-- Legend -->
          <div style="display: flex; justify-content: center; gap: 24px; margin-top: 14px; font-size: 0.85rem; font-weight: 700; flex-wrap: wrap;">
            <span style="display: inline-flex; align-items: center; gap: 6px; color: ${tokens.color.morrisIndigo};">
              <span style="display: inline-block; width: 14px; height: 3px; background: #00E5FF; border-radius: 2px;"></span>
              Accuracy %
            </span>
            <span style="display: inline-flex; align-items: center; gap: 6px; color: ${tokens.color.madderCoral};">
              <span style="display: inline-block; width: 10px; height: 10px; background: ${tokens.color.madderCoral}; border-radius: 50%;"></span>
              Impulsive Guesses (&lt;1.5s)
            </span>
          </div>
        </div>

        <!-- 3-Segment Metrics Grid -->
        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 14px; margin-bottom: 22px;">
          ${segments.map((s, idx) => {
            const isFatigued = idx === 2 && fatigueOnsetDetected;
            return `
              <div style="padding: 16px; background: rgba(255, 253, 249, 0.95); border: 1.5px solid ${isFatigued ? tokens.color.madderCoral : tokens.color.gildedBrass}; border-radius: 12px; text-align: center;">
                <span style="display: block; font-size: 0.8rem; font-weight: 800; color: ${tokens.color.seaweedSage}; text-transform: uppercase; margin-bottom: 4px;">
                  ${s.label}
                </span>
                <div style="font-size: 1.6rem; font-weight: 800; color: ${isFatigued ? tokens.color.madderCoral : tokens.color.morrisIndigo}; margin-bottom: 6px;">
                  ${s.totalAttempts > 0 ? `${s.accuracyPercent}%` : '—'}
                </div>
                <div style="font-size: 0.85rem; color: #4A5B73;">
                  <div>${s.totalAttempts} questions</div>
                  <div style="color: ${s.impulsivePercent > 20 ? tokens.color.madderCoral : tokens.color.seaweedSage}; font-weight: 700;">
                    ${s.impulsiveCount} rapid (${s.impulsivePercent}%)
                  </div>
                </div>
              </div>
            `;
          }).join('')}
        </div>

        <!-- Parent Recommendation Banner -->
        <div style="padding: 18px 22px; background: rgba(255, 246, 229, 0.9); border-left: 4px solid ${fatigueOnsetDetected ? tokens.color.madderCoral : tokens.color.gildedBrass}; border-radius: 10px;">
          <div style="font-weight: 800; font-size: 0.95rem; color: ${fatigueOnsetDetected ? tokens.color.madderCoral : tokens.color.morrisIndigo}; margin-bottom: 4px;">
            ${fatigueOnsetDetected ? '⚠️ Cognitive Fatigue Detected' : '💡 Field Guide Observation for Florin:'}
          </div>
          <p style="margin: 0; font-size: 0.95rem; line-height: 1.6; color: ${tokens.color.morrisIndigo};">
            ${parentInsight}
          </p>
        </div>
      </section>
    `;

    this.attachEventListeners();
    return this.container;
  }

  private attachEventListeners(): void {
    const exportBtn = this.container.querySelector<HTMLButtonElement>('#btn-export-telemetry-json');
    exportBtn?.addEventListener('click', () => {
      soundEngine.playTap();
      TelemetryCollector.triggerJsonDownload();
    });
  }

  private renderSvgChart(): string {
    const { segments } = this.analysis;
    const w = this.width;
    const h = this.height;
    const padX = 50;
    const padY = 30;
    const chartW = w - padX * 2;
    const chartH = h - padY * 2;

    // 3 x positions for segments
    const xStep = chartW / 2;
    const xPoints = [padX, padX + xStep, padX + chartW];

    // Y scale: 0% at bottom (padY + chartH), 100% at top (padY)
    const getY = (percent: number) => {
      const clamped = Math.max(0, Math.min(100, percent));
      return padY + chartH - (clamped / 100) * chartH;
    };

    // Horizontal grid lines
    const gridLines = [0, 25, 50, 75, 100].map((val) => {
      const y = getY(val);
      return `
        <line x1="${padX}" y1="${y}" x2="${padX + chartW}" y2="${y}" stroke="rgba(26, 54, 93, 0.1)" stroke-width="1" />
        <text x="${padX - 8}" y="${y + 4}" text-anchor="end" font-family="${tokens.font.mono}" font-size="9" fill="rgba(26, 54, 93, 0.45)">${val}%</text>
      `;
    }).join('');

    // Accuracy Line Coordinates
    const accuracyPoints = segments.map((s, i) => {
      const pct = s.totalAttempts > 0 ? s.accuracyPercent : 80;
      return { x: xPoints[i]!, y: getY(pct), pct, total: s.totalAttempts };
    });

    const accuracyPathD = `M ${accuracyPoints.map((p) => `${p.x},${p.y}`).join(' L ')}`;

    // Impulsive Points
    const impulsiveMarkers = segments.map((s, i) => {
      const x = xPoints[i]!;
      const y = getY(s.impulsivePercent);
      return `
        <circle cx="${x}" cy="${y}" r="6" fill="${tokens.color.madderCoral}" stroke="#FFF" stroke-width="2" />
        <text x="${x}" y="${y - 10}" text-anchor="middle" font-family="${tokens.font.mono}" font-size="10" font-weight="800" fill="${tokens.color.madderCoral}">
          ${s.impulsivePercent}%
        </text>
      `;
    }).join('');

    // X Axis Labels
    const xLabels = segments.map((s, i) => `
      <text x="${xPoints[i]}" y="${padY + chartH + 20}" text-anchor="middle" font-family="${tokens.font.body}" font-size="11" font-weight="700" fill="${tokens.color.morrisIndigo}">
        ${s.label}
      </text>
    `).join('');

    return `
      <svg
        viewBox="0 0 ${w} ${h}"
        width="100%"
        max-width="${w}px"
        style="overflow: visible; display: block; margin: 0 auto;"
        aria-label="Cognitive Fatigue Chart"
      >
        <defs>
          <linearGradient id="accuracyGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stop-color="#00E5FF" stop-opacity="0.3" />
            <stop offset="100%" stop-color="#1A365D" stop-opacity="0.0" />
          </linearGradient>
        </defs>

        <!-- Grid Lines -->
        ${gridLines}

        <!-- Accuracy Fill Area -->
        <polygon
          points="${padX},${padY + chartH} ${accuracyPoints.map((p) => `${p.x},${p.y}`).join(' ')} ${padX + chartW},${padY + chartH}"
          fill="url(#accuracyGradient)"
        />

        <!-- Accuracy Trend Line -->
        <path d="${accuracyPathD}" fill="none" stroke="${tokens.color.morrisIndigo}" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" />

        <!-- Accuracy Data Vertices -->
        ${accuracyPoints.map((p) => `
          <circle cx="${p.x}" cy="${p.y}" r="5" fill="#00E5FF" stroke="${tokens.color.morrisIndigo}" stroke-width="2" />
          <text x="${p.x}" y="${p.y - 10}" text-anchor="middle" font-family="${tokens.font.mono}" font-size="11" font-weight="800" fill="${tokens.color.morrisIndigo}">
            ${p.total > 0 ? `${p.pct}%` : '—'}
          </text>
        `).join('')}

        <!-- Impulsive Guess Markers -->
        ${impulsiveMarkers}

        <!-- X Labels -->
        ${xLabels}
      </svg>
    `;
  }

  getElement(): HTMLElement {
    return this.container;
  }
}
